import {
  BookOpen,
  Briefcase,
  Building2,
  ChartColumn,
  CircleHelp,
  CodeXml,
  Coffee,
  Crown,
  Disc3,
  Dumbbell,
  Factory,
  Flag,
  Flower2,
  Gem,
  Gift,
  Layers,
  MapPin,
  Megaphone,
  MessageCircle,
  MessageSquareQuote,
  Network,
  Newspaper,
  Package,
  Percent,
  Plug,
  QrCode,
  Sandwich,
  Scissors,
  Share2,
  Shirt,
  ShoppingBasket,
  ShoppingCart,
  Star,
  Store,
  Ticket,
  Trophy,
  Tv,
  UtensilsCrossed,
  Wallet,
  type LucideIcon,
} from "lucide-react";

export interface NavLink {
  label: string;
  route: string;
  icon?: LucideIcon;
  // One-liner under the label - only on the larger left-column rows, so the
  // icon grids stay compact single-line rows.
  description?: string;
}

// Products menu: a list of product areas on the left, a grid of loyalty
// program types on the right, and a "customized solution" promo under it.
export interface ProductsPanel {
  kind: "products";
  sectionLabel: string;
  links: NavLink[];
  gridLabel: string;
  gridLinks: NavLink[];
  // `route` makes the title itself a link (the tailored-solutions page);
  // `cta` is the separate pill button.
  promo: { title: string; route: string; description: string; cta: NavLink };
  // "View all products ->" under the left column.
  allLink?: NavLink;
}

// One row on the left of a categories panel. Hovering/focusing it swaps the
// right-hand side to its detail: an icon grid (`links`), a bulleted link list
// (`posts`), or plain bullet notes (`notes`) when the category has a single
// page and a link grid would just repeat it.
export interface NavCategory {
  label: string;
  description: string;
  icon: LucideIcon;
  // Unset when there's no hub page yet (e.g. Guides) - the row then only
  // switches the detail view instead of navigating.
  route?: string;
  links?: NavLink[];
  posts?: NavLink[];
  notes?: string[];
  exploreLabel?: string;
}

// Solutions and Resources menus: categories on the left, the active
// category's detail on the right.
export interface CategoriesPanel {
  kind: "categories";
  sectionLabel: string;
  categories: NavCategory[];
  // Highlighted band under the category list (e.g. "different industry?").
  footerLink?: NavLink;
  // "View all solutions ->" under the category list.
  allLink?: NavLink;
}

export interface NavDropdown {
  label: string;
  panel: ProductsPanel | CategoriesPanel;
}

export type NavEntry = NavLink | NavDropdown;

export function isNavDropdown(item: NavEntry): item is NavDropdown {
  return "panel" in item;
}

// Items, labels and order follow the Website 2.0 preview's menu exactly.
// Preview items whose page isn't built yet are left out until it ships
// (never link to a missing page).
export const navItems: NavEntry[] = [
  {
    label: "Products",
    panel: {
      kind: "products",
      sectionLabel: "Products",
      links: [
        {
          label: "Loyalty Programs",
          route: "/products/loyalty-programs",
          icon: Gift,
          description: "Cashback, points, tiers and ways to earn.",
        },
        {
          label: "Marketing Campaigns",
          route: "/products/marketing-campaigns",
          icon: Megaphone,
          description: "Offers, occasions and win-back.",
        },
        {
          label: "Customer Intelligence",
          route: "/products/customer-intelligence",
          icon: ChartColumn,
          description: "See who visits and who is slipping away.",
        },
        {
          label: "WhatsApp Marketing",
          route: "/whatsapp-loyalty-marketing",
          icon: MessageCircle,
          description: "Reach customers where they already chat.",
        },
        {
          label: "Integrations & Infrastructure",
          route: "/pos-loyalty-integration",
          icon: Plug,
          description: "Connect Fydo to the tools you use.",
        },
      ],
      gridLabel: "Loyalty programs",
      // Same items, in the same order, as the Website 2.0 preview's menu.
      gridLinks: [
        { label: "Cashback", route: "/cashback-loyalty-program", icon: Wallet },
        { label: "Points", route: "/loyalty-points-software", icon: Star },
        { label: "Tiers", route: "/tiered-loyalty-program", icon: Layers },
        { label: "Membership", route: "/loyalty-membership-program", icon: Crown },
        { label: "Milestones", route: "/tiered-loyalty-program#milestones", icon: Flag },
        { label: "Discounts", route: "/discount-loyalty-program", icon: Percent },
        { label: "Rewards", route: "/customer-reward-program", icon: Gift },
        { label: "QR Loyalty", route: "/qr-code-loyalty-program", icon: QrCode },
        { label: "Referral", route: "/referral-rewards-software", icon: Share2 },
        { label: "Spin & Win", route: "/spin-the-wheel-loyalty", icon: Disc3 },
        { label: "Scratch & Win", route: "/scratch-and-win-loyalty", icon: Ticket },
      ],
      promo: {
        title: "Need a Customized Loyalty Solution?",
        route: "/tailored-loyalty-solutions",
        description:
          "Tell us what your business needs. Our team will help shape a suitable solution.",
        cta: { label: "Discuss Your Requirements", route: "/contact" },
      },
      allLink: { label: "View all products", route: "/products" },
    },
  },
  {
    label: "Solutions",
    panel: {
      kind: "categories",
      sectionLabel: "Industries",
      categories: [
        {
          label: "Retail & Stores",
          description: "Jewellery, grocery, fashion and more.",
          icon: Store,
          route: "/loyalty-program-for-retail-stores",
          exploreLabel: "All Retail & Stores ideas",
          links: [
            { label: "Jewellery", route: "/loyalty-program-for-jewellery-store", icon: Gem },
            { label: "Grocery & General Stores", route: "/loyalty-program-for-grocery-store", icon: ShoppingBasket },
            { label: "Fashion & Apparel", route: "/loyalty-app-for-clothing-store", icon: Shirt },
            { label: "Electronics", route: "/loyalty-program-for-electronics-store", icon: Tv },
            { label: "Other Retail Stores", route: "/loyalty-program-for-retail-stores", icon: Store },
          ],
        },
        {
          label: "Restaurants & QSRs",
          description: "Restaurants, cafés and quick-service.",
          icon: UtensilsCrossed,
          route: "/customer-retention-for-restaurants",
          exploreLabel: "All Restaurants & QSRs ideas",
          links: [
            { label: "Restaurants", route: "/customer-retention-for-restaurants", icon: UtensilsCrossed },
            { label: "Cafés & Bakeries", route: "/customer-retention-for-restaurants#cafes", icon: Coffee },
            { label: "Quick-Service Restaurants (QSRs)", route: "/customer-retention-for-restaurants#qsr", icon: Sandwich },
          ],
        },
        {
          label: "Salons, Wellness & Services",
          description: "Salons, spas and gyms.",
          icon: Scissors,
          route: "/loyalty-software-for-salon",
          exploreLabel: "All Salons, Wellness & Services ideas",
          links: [
            { label: "Salons", route: "/loyalty-software-for-salon", icon: Scissors },
            { label: "Spas & Beauty", route: "/loyalty-program-for-spas", icon: Flower2 },
            { label: "Gyms & Fitness", route: "/loyalty-program-for-gyms", icon: Dumbbell },
          ],
        },
        {
          label: "E-commerce & D2C",
          description: "Online stores and direct brands.",
          icon: ShoppingCart,
          route: "/solutions/ecommerce-d2c",
          exploreLabel: "All E-commerce & D2C ideas",
          links: [
            { label: "E-commerce B2C", route: "/loyalty-program-for-ecommerce", icon: ShoppingCart },
            { label: "E-commerce B2B", route: "/loyalty-program-for-b2b-ecommerce", icon: Briefcase },
            { label: "Direct-to-Consumer (D2C) Brands", route: "/solutions/ecommerce-d2c#d2c", icon: Package },
          ],
        },
        {
          label: "Consumer Brands & Manufacturing",
          description: "Reach the end buyer.",
          icon: Factory,
          route: "/solutions/consumer-brands-manufacturing",
          exploreLabel: "All Consumer Brands & Manufacturing ideas",
          links: [
            { label: "Manufacturing", route: "/loyalty-program-for-manufacturing", icon: Factory },
            { label: "FMCG & Consumer Brands", route: "/loyalty-program-for-consumer-brands", icon: Package },
          ],
        },
        {
          label: "Enterprise & Multi-Location",
          description: "Many outlets, one program.",
          icon: Building2,
          route: "/solutions/enterprise-multi-location",
          exploreLabel: "All Enterprise & Multi-Location ideas",
          links: [
            { label: "Multi-Location Businesses", route: "/loyalty-program-for-multi-location-businesses", icon: MapPin },
            { label: "Enterprise", route: "/loyalty-program-for-enterprise", icon: Building2 },
            { label: "Franchise Businesses", route: "/solutions/franchise-loyalty", icon: Network },
          ],
        },
      ],
      allLink: { label: "View all solutions", route: "/solutions" },
      footerLink: { label: "Have a different industry? Let's talk.", route: "/contact" },
    },
  },
  {
    label: "Resources",
    panel: {
      kind: "categories",
      sectionLabel: "Resources",
      categories: [
        {
          label: "Blog",
          description: "Articles on loyalty and retention.",
          icon: Newspaper,
          route: "/blogs",
          exploreLabel: "Open the blog",
          posts: [
            {
              label: "Top 10 best loyalty program software for all businesses in 2026-2027",
              route: "/blogs/top-10-best-loyalty-program-software-for-all-businesses-in-2026-2027",
            },
            {
              label: "Customer loyalty trends for retail businesses in Tier 2 Indian cities",
              route: "/blogs/customer-loyalty-trends-for-retail-businesses-in-tier-2-indian-cities",
            },
            {
              label: "Loyalty programs for medical stores and pharmacies in India",
              route: "/blogs/loyalty-programs-for-medical-stores-and-pharmacies-in-india",
            },
          ],
        },
        {
          label: "Guides",
          description: "Practical how-to guides.",
          icon: BookOpen,
          route: "/guides",
          exploreLabel: "Open Guides",
          posts: [
            { label: "Plan your first programme", route: "/how-to-plan-a-loyalty-program" },
            { label: "Record a bill at the counter", route: "/how-to-record-a-bill-with-fydo" },
            { label: "Read your customer data", route: "/how-to-read-your-customer-data" },
            { label: "Segment your customers", route: "/how-to-segment-customers" },
            { label: "Create a WhatsApp campaign", route: "/how-to-create-a-whatsapp-campaign" },
            { label: "Increase repeat purchases", route: "/how-to-increase-repeat-purchases" },
          ],
        },
        {
          label: "Case Studies",
          description: "Customer results.",
          icon: Trophy,
          route: "/case-studies",
          exploreLabel: "Open Case Studies",
        },
        {
          label: "Customer Stories",
          description: "Stories from Fydo customers.",
          icon: MessageSquareQuote,
          route: "/customer-stories",
          exploreLabel: "Open Customer Stories",
        },
        {
          label: "Industry Insights",
          description: "Perspectives on retail and loyalty.",
          icon: ChartColumn,
          route: "/industry-insights",
          exploreLabel: "Open Industry Insights",
          posts: [
            { label: "Jewellery", route: "/industry-insights#jewellery" },
            { label: "Grocery", route: "/industry-insights#grocery" },
            { label: "Fashion", route: "/industry-insights#fashion" },
            { label: "Restaurants and cafés", route: "/industry-insights#restaurants" },
            { label: "Salons and beauty", route: "/industry-insights#salons" },
            { label: "Industry report", route: "/industry-insights#report" },
          ],
        },
        {
          label: "FAQs",
          description: "Answers to common questions.",
          icon: CircleHelp,
          route: "/faqs",
          exploreLabel: "Open FAQs",
        },
        {
          label: "Documentation",
          description: "Technical docs for builders.",
          icon: CodeXml,
          route: "/documentation",
          exploreLabel: "Open Documentation",
        },
      ],
      allLink: { label: "View all resources", route: "/resources" },
    },
  },
  { label: "Pricing", route: "/pricing" },
];

export const headerCta: NavLink = {
  label: "Get a Free Demo",
  route: "/partner-signup",
};

// Every route a dropdown panel can link to, used for its trigger's
// "current section" highlight.
export function panelRoutes(panel: NavDropdown["panel"]): string[] {
  const allRoute = panel.allLink ? [panel.allLink.route] : [];
  if (panel.kind === "products") {
    return [
      ...[...panel.links, ...panel.gridLinks].map((link) => link.route),
      panel.promo.route,
      ...allRoute,
    ];
  }
  return [...allRoute, ...panel.categories.flatMap((category) => [
    ...(category.route ? [category.route] : []),
    ...[...(category.links ?? []), ...(category.posts ?? [])].map(
      (link) => link.route,
    ),
  ])];
}

// Flat, de-duplicated list of every nav destination, used by the root layout
// to build the hover/focus prefetch speculation rules - kept here so that
// list stays in sync with navItems automatically. In-page anchors
// (`/page#section`) are reduced to their page so it's prefetched once.
export const allNavRoutes: string[] = Array.from(
  new Set(
    navItems
      .flatMap((item) =>
        isNavDropdown(item) ? panelRoutes(item.panel) : [item.route],
      )
      .map((route) => route.split("#")[0]),
  ),
);
